/**
 * Kartenvorschläge für die Kachel im Deck-Editor (Admin-Werkzeug).
 *
 * Der Vorschlag wird von außen über POST /api/deck-suggestions abgelegt und hier
 * per Polling abgeholt. Für Nicht-Admins läuft die Query nie (`enabled`), damit
 * niemand ohne das Feature Dauer-Anfragen erzeugt.
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { json } from '../../../api/apiClient';
import { useAuth } from '../../../context/AuthContext';

const POLL_INTERVAL_MS = 5000;

/**
 * Ein Vorschlag gilt genau einem Deck — für jedes andere ist er unsichtbar. Beide Seiten
 * des Vergleichs sind UI-Deck-IDs („virtual_25"): so legt der Absender den Vorschlag ab
 * (`deckSuggestionController.create` verlangt eine Zeichenkette). Eine numerische ID
 * trifft hier nie.
 */
export function selectSuggestionIds(suggestion, currentDeckId) {
  if (!suggestion || !currentDeckId) return [];
  if (suggestion.deckId !== currentDeckId) return [];
  return Array.isArray(suggestion.cardIds) ? suggestion.cardIds : [];
}

/**
 * Stabiler Schlüssel für das Nachladen der Kartendaten: sortiert, damit ein
 * unveränderter Vorschlag bei jedem Poll denselben Schlüssel ergibt und die
 * Kartendaten nicht alle fünf Sekunden neu geladen werden.
 */
export function suggestionQueryKey(cardIds) {
  return [...cardIds].sort();
}

/**
 * Ordnet die Kartendaten in die Reihenfolge des Vorschlags um. Die API
 * liefert Kartendaten nicht in der angeforderten Reihenfolge zurück —
 * diese Funktion stellt sie wieder her und verwirft unbekannte IDs.
 */
export function orderCardsBySuggestion(cards, cardIds) {
  if (!Array.isArray(cards) || !Array.isArray(cardIds)) return [];
  const nachId = new Map((cards || []).map((c) => [c.id, c]));
  return cardIds.map((id) => nachId.get(id)).filter(Boolean);
}

export function useCardSuggestions(deckId) {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const { data: suggestion } = useQuery({
    queryKey: ['deckSuggestions'],
    queryFn: async () => (await json('/api/deck-suggestions')).suggestion ?? null,
    enabled: Boolean(isAdmin),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  const cardIds = selectSuggestionIds(suggestion, deckId);

  const { data: cards } = useQuery({
    queryKey: ['deckSuggestionCards', suggestionQueryKey(cardIds)],
    queryFn: async () => {
      const { cards: geladen } = await json('/api/cards/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardIds }),
      });
      return orderCardsBySuggestion(geladen, cardIds);
    },
    enabled: Boolean(isAdmin) && cardIds.length > 0,
    staleTime: Infinity,
  });

  const verwerfen = useCallback(async (query) => {
    await json(`/api/deck-suggestions${query}`, { method: 'DELETE' });
    queryClient.invalidateQueries({ queryKey: ['deckSuggestions'] });
  }, [queryClient]);

  const dismissAll = useCallback(() => verwerfen(''), [verwerfen]);
  const dismissCard = useCallback(
    (cardId) => verwerfen(`?cardId=${encodeURIComponent(cardId)}`),
    [verwerfen],
  );

  return { cards: cards || [], dismissAll, dismissCard };
}
